import React, { FunctionComponent, useEffect, useState } from "react";
import { Container, Nav, Navbar as BootstrapNavbar } from "react-bootstrap";
import { Link, useNavigate, useLocation } from "react-router";
import {
  faAlignJustify,
  faCog,
  faEnvelope,
  faEnvelopeOpenText,
  faHome,
  faPowerOff,
  faPuzzlePiece,
  faUsers,
  faUserShield
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import WebtieLogo from "../webtie-logo.png";
import "./navbarStyle.scss";
import { useAccessibleUsersIds } from "../../../hooks/useAccessibleUsersIds";
import { useAuthUser } from "../../../hooks/useAuthUser";

interface Props {
  userId: string;
  isAdmin: boolean;
  signOut: () => Promise<void>;
}

const Navbar: FunctionComponent<Props> = ({ userId, isAdmin, signOut }: Props) => {
  const location = useLocation();
  const pathname = location.pathname;
  const [width, setWidth] = useState(window.innerWidth);
  const isMobile = width < 1400;
  const navigate = useNavigate();
  const { user: authUser } = useAuthUser();
  const { userIds } = useAccessibleUsersIds(authUser?.uid);

  const hasAccessToOtherUsers = userIds.length > 0;

  const updateDimensions = (): void => {
    setWidth(window.innerWidth);
  };
  useEffect(() => {
    window.addEventListener("resize", updateDimensions);
    return () => window.removeEventListener("resize", updateDimensions);
  }, []);

  return (
    <BootstrapNavbar bg="light" expand="xxl" className={"mb-2"}>
      <Container>
        <Nav.Link className={"ps-0"} as={Link} to={`/user/${userId}/dashboard`}>
          <BootstrapNavbar.Brand>
            <img src={WebtieLogo} height={45} alt={"Webtie Logo"} />
          </BootstrapNavbar.Brand>
        </Nav.Link>
        <BootstrapNavbar.Toggle aria-controls="basic-navbar-nav" />
        <BootstrapNavbar.Collapse id="basic-navbar-nav">
          <Nav className="ms-0 pt-3 pt-lg-0 me-auto text-end text-lg-start">
            <Nav.Link
              active={pathname === `/user/${userId}/dashboard`}
              className={"me-lg-3 my-1 my-lg-0"}
              as={Link}
              to={`/user/${userId}/dashboard`}>
              {isMobile && "Dashboard"}{" "}
              <FontAwesomeIcon icon={faHome} style={isMobile ? { marginLeft: 5 } : {}} className={"me-lg-1"} />{" "}
              {!isMobile && "Dashboard"}
            </Nav.Link>

            <Nav.Link
              active={pathname === `/user/${userId}/registrations`}
              className={"me-lg-3 my-1 my-lg-0"}
              as={Link}
              to={`/user/${userId}/registrations`}>
              {isMobile && "Anmeldungen"}
              <FontAwesomeIcon
                icon={faEnvelopeOpenText}
                style={isMobile ? { marginLeft: 5 } : {}}
                className={"me-lg-1"}
              />{" "}
              {!isMobile && "Anmeldungen"}
            </Nav.Link>
            <Nav.Link
              active={pathname === `/user/${userId}/limits`}
              className={"me-lg-3 my-1 my-lg-0"}
              as={Link}
              to={`/user/${userId}/limits`}>
              {isMobile && "Angebot"}{" "}
              <FontAwesomeIcon icon={faPuzzlePiece} style={isMobile ? { marginLeft: 5 } : {}} className={"me-lg-1"} />{" "}
              {!isMobile && "Angebot"}
            </Nav.Link>
            <Nav.Link
              active={pathname === `/user/${userId}/form`}
              className={"me-lg-3 my-1 my-lg-0"}
              as={Link}
              to={`/user/${userId}/form`}>
              {isMobile && "Formular"}{" "}
              <FontAwesomeIcon icon={faAlignJustify} style={isMobile ? { marginLeft: 5 } : {}} className={"me-lg-1"} />{" "}
              {!isMobile && "Formular"}
            </Nav.Link>
            <Nav.Link
              active={pathname === `/user/${userId}/email`}
              className={"me-lg-3 my-1 my-lg-0"}
              as={Link}
              to={`/user/${userId}/email`}>
              {isMobile && "E-Mail"}{" "}
              <FontAwesomeIcon icon={faEnvelope} style={isMobile ? { marginLeft: 5 } : {}} className={"me-lg-1"} />{" "}
              {!isMobile && "E-Mail"}
            </Nav.Link>
          </Nav>
          <Nav className="text-end text-lg-start d-flex flex-row align-items-center">
            {isAdmin && (
              <Nav.Link
                className="my-1 my-lg-0 me-1"
                active={pathname === `/user/${userId}/admin`}
                as={Link}
                to={`/user/${userId}/admin`}>
                {isMobile && "Admin"} <FontAwesomeIcon icon={faUserShield} style={isMobile ? { marginLeft: 5 } : {}} />
              </Nav.Link>
            )}

            {hasAccessToOtherUsers && (
              <Nav.Link
                className="my-1 my-lg-0 me-1"
                active={pathname === `/user/${userId}/user-management`}
                as={Link}
                to={`/user/${userId}/user-management`}>
                {isMobile && "User Verwaltung"}
                <FontAwesomeIcon icon={faUsers} style={isMobile ? { marginLeft: 5 } : {}} />
              </Nav.Link>
            )}
            <Nav.Link
              className="my-1 my-lg-0 me-1"
              active={pathname === `/user/${userId}/settings`}
              as={Link}
              to={`/user/${userId}/settings`}>
              {isMobile && "Einstellungen"} <FontAwesomeIcon icon={faCog} style={isMobile ? { marginLeft: 5 } : {}} />
            </Nav.Link>

            <Nav.Link as={Link} to={"/"} className="pe-0 my-1 my-lg-0">
              <span
                className="log-out-link"
                onClick={() => {
                  signOut().then(() => navigate("/"));
                }}>
                {isMobile && "Abmelden"} <FontAwesomeIcon icon={faPowerOff} style={isMobile ? { marginLeft: 5 } : {}} />
              </span>
            </Nav.Link>
          </Nav>
        </BootstrapNavbar.Collapse>
      </Container>
    </BootstrapNavbar>
  );
};

export default Navbar;
